import axios from 'axios';
import classNames from 'classnames';
import Button from 'components/base/Button';
import ExportFoodDataModal from 'components/modals/ExportFoodDataModal';
import * as formik from 'formik';
import { errorHandler } from 'helpers/response-utils';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { patientValidationSchma } from 'static/Patient';
import * as yup from 'yup';
import { parseData } from 'helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { trackEvent } from '../../../utils/analytics';

const EditPatient = () => {
  const navigate = useNavigate();
  const { Formik, useFormik } = formik;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(false);
  const [dietitians, setDietitians] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const { id } = useParams(); // Assuming you're using React Router for navigation
  const token = localStorage.getItem('token');
  const currentUser = parseData(localStorage.getItem('user') || '{}');

  // Extract clinic ID, handling both object and primitive types
  const userClinicId = currentUser?.clinic
    ? typeof currentUser.clinic === 'object'
      ? currentUser.clinic.id
      : currentUser.clinic
    : undefined;

  const isClinicUser =
    String(currentUser?.role || '').toLowerCase() === 'clinic';
  const genderArray = ['male', 'female'];
  const [patient, setPatient] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    gender: '',
    fileID: '',
    birthdate: '',
    isDisplayBloodSugar: false,
    isDisplayNutritionalInfo: false,
    dietitian: ''
  });
  const schema = yup.object().shape(patientValidationSchma);

  useEffect(() => {
    getPatient();
    if (isClinicUser) {
      getDietitians();
    }
  }, []);

  const getDietitians = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/clinic/dietitians`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        toast.error(response.data.error.message);
        return;
      }

      setDietitians(
        response.data.data.users || response.data.data.dietitians || []
      );
    } catch (error) {
      console.error('Error occurred while loading dietitians:', error);
    }
  };

  const getPatient = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/clinic/patient/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        errorHandler(response.data);
        return;
      }
      const data = response.data.data.patient;
      setPatient({
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        notes: data.notes,
        gender: data.gender,
        fileID: data.fileID,
        birthdate: moment(data.birthdate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        isDisplayBloodSugar: data.isDisplayBloodSugar,
        isDisplayNutritionalInfo: data.isDisplayNutritionalInfo,
        dietitian:
          typeof data.dietitian === 'object'
            ? data.dietitian?.id || ''
            : data.dietitian || ''
      });
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const onSubmit = async (values: any) => {
    const data = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      address: values.address,
      notes: values.notes,
      gender: values.gender,
      fileID: values.fileID,
      birthdate: values.birthdate,
      isDisplayBloodSugar: values.isDisplayBloodSugar,
      isDisplayNutritionalInfo: values.isDisplayNutritionalInfo,
      ...(isClinicUser
        ? { dietitian: values.dietitian ? Number(values.dietitian) : null }
        : {})
    };

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_ENDPOINT}/clinic/patient/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === false) {
        toast.error(response.data.error.message);
        return;
      }

      const actorId = currentUser?.id;

      const actorName = `${currentUser?.firstname || ''} ${
        currentUser?.lastname || ''
      }`.trim();

      const actorRole =
        String(currentUser?.role || '').toLowerCase() === 'clinic'
          ? 'clinic admin'
          : currentUser?.role;

      trackEvent('profile_updated', {
        actor_user_id: String(actorId),
        actor_user_name: actorName,
        actor_role: actorRole,
        platform: 'web'
      });

      toast(response.data.data.message);
      setIsEditing(false);

      navigateBack();
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const navigateBack = () => {
    navigate('/clinic/patient/list');
  };

  const handleNutritionalToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
    values: any,
    setValues: any
  ) => {
    const newValue = e.target.checked;

    if (newValue && !values.isDisplayNutritionalInfo) {
      // OFF → ON
      setPendingToggle(true);
      setShowConfirmModal(true);
    } else {
      // ON → OFF
      setValues({
        ...values,
        isDisplayNutritionalInfo: false
      });
    }
  };

  return (
    <Row>
      <Col xl={12}>
        <div className="d-flex align-items-center mb-3">
          {currentUser?.role !== 'DIETITIAN' &&
            (currentUser?.clinical_export_enabled ||
              currentUser?.research_export_enabled) && (
              <Button
                variant="secondary"
                startIcon={
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                }
                onClick={() => setShowExportModal(true)}
              >
                Export Patient Data
              </Button>
            )}
          {!isEditing && (
            <Button
              type="button"
              variant="primary"
              className="ms-auto"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>
        <Formik
          validationSchema={schema}
          onSubmit={values => onSubmit(values)}
          initialValues={{
            name: '',
            email: '',
            phone: '',
            address: '',
            notes: '',
            gender: '',
            fileID: '',
            birthdate: '',
            isDisplayBloodSugar: false,
            isDisplayNutritionalInfo: false,
            dietitian: ''
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            dirty,
            setValues
          }) => {
            useEffect(() => {
              if (patient.name) {
                setValues(patient);
              }
            }, [patient]);
            return (
              <>
                <Form noValidate onSubmit={handleSubmit}>
                  <Row className={classNames('g-3', 'mb-6')}>
                    <Col sm={6} md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Fullname</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          disabled={!isEditing}
                          isValid={touched.name && !errors.name}
                          isInvalid={touched.name && !!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {' '}
                          {errors.name}{' '}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    {isClinicUser && (
                      <Col sm={6} md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Dietitian</Form.Label>
                          <Form.Select
                            name="dietitian"
                            value={values.dietitian}
                            onChange={handleChange}
                            disabled={!isEditing}
                            style={
                              !isEditing
                                ? {
                                    backgroundColor:
                                      'rgba(var(--phoenix-gray-100-rgb), 0.7)',
                                    color: 'var(--phoenix-quaternary-color)'
                                  }
                                : undefined
                            }
                          >
                            <option value="">-- Select --</option>
                            {dietitians.map(dietitian => (
                              <option value={dietitian.id} key={dietitian.id}>
                                {dietitian.firstname} ({dietitian.email}) -{' '}
                                {String(dietitian.role).toLowerCase() ===
                                'clinic'
                                  ? 'Clinic Admin'
                                  : 'Dietitian'}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                    <Col sm={6} md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          disabled={!isEditing}
                          isValid={touched.email && !errors.email}
                          isInvalid={touched.email && !!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {' '}
                          {errors.email}{' '}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={6} md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Contact</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone"
                          value={values.phone}
                          onChange={handleChange}
                          disabled={!isEditing}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>file ID</Form.Label>
                        <Form.Control
                          type="text"
                          name="fileID"
                          value={values.fileID}
                          onChange={handleChange}
                          disabled={!isEditing}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Birthdate</Form.Label>
                        <Form.Control
                          type="date"
                          name="birthdate"
                          value={values.birthdate}
                          onChange={handleChange}
                          disabled={!isEditing}
                          isValid={touched.birthdate && !errors.birthdate}
                          isInvalid={touched.birthdate && !!errors.birthdate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {' '}
                          {errors.birthdate}{' '}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={6} md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Gender</Form.Label>
                        <Form.Select
                          name="gender"
                          value={values.gender}
                          onChange={handleChange}
                          disabled={!isEditing}
                          style={
                            !isEditing
                              ? {
                                  backgroundColor:
                                    'rgba(var(--phoenix-gray-100-rgb), 0.7)',
                                  color: 'var(--phoenix-quaternary-color)'
                                }
                              : undefined
                          }
                          isValid={touched.gender && !errors.gender}
                          isInvalid={touched.gender && !!errors.gender}
                        >
                          <option value=""> -- Select -- </option>
                          {genderArray.length
                            ? genderArray.map((gender, key) => (
                                <option value={gender} key={key}>
                                  {' '}
                                  {gender.charAt(0).toUpperCase() +
                                    gender.slice(1)}{' '}
                                </option>
                              ))
                            : ''}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {' '}
                          {errors.gender}{' '}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={12}>
                      <Form.Group className="mb-2">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="address"
                          value={values.address}
                          onChange={handleChange}
                          disabled={!isEditing}
                          rows={4}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={12}>
                      <Form.Group className="mb-2">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="notes"
                          value={values.notes}
                          onChange={handleChange}
                          disabled={!isEditing}
                          rows={4}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={12}>
                      <Form.Group className="mb-2">
                        <Form.Check
                          type="switch"
                          name="isDisplayBloodSugar"
                          id="isDisplayBloodSugar"
                          label="Would you like to know your patient blood sugar?"
                          checked={values.isDisplayBloodSugar}
                          disabled={!isEditing}
                          onChange={e =>
                            setValues({
                              ...values,
                              isDisplayBloodSugar: e.target.checked
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={12} md={12}>
                      <Form.Group className="mb-2">
                        <Form.Check
                          type="switch"
                          name="isDisplayNutritionalInfo"
                          id="isDisplayNutritionalInfo"
                          label="Would you like your patient to see the food nutritional information?"
                          checked={values.isDisplayNutritionalInfo}
                          disabled={!isEditing}
                          onChange={e =>
                            handleNutritionalToggle(e, values, setValues)
                          }
                        />
                      </Form.Group>
                    </Col>

                    {isEditing && (
                      <div className="text-end">
                        <Button
                          type="button"
                          variant="phoenix-primary"
                          className="me-2 px-6"
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                          Update
                        </Button>
                      </div>
                    )}
                  </Row>
                </Form>
                <Modal
                  show={showConfirmModal}
                  onHide={() => setShowConfirmModal(false)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Important Notice: Patient Safety</Modal.Title>
                  </Modal.Header>

                  <Modal.Body>
                    <p>
                      Before enabling the camera-based food diary for your
                      patient, please be aware:
                    </p>

                    <ul>
                      <li>
                        Ensure that this feature is suitable for your patient’s
                        needs.
                      </li>
                      <li>
                        Monitor your patient for signs of eating disorders;
                        professional judgment is essential.
                      </li>
                      <li>
                        This tool complements your expertise but does not
                        replace it.
                      </li>
                    </ul>

                    <p>
                      By proceeding, you acknowledge and accept full
                      responsibility for your patient’s safety.
                    </p>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowConfirmModal(false);
                        setPendingToggle(false);
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="primary"
                      onClick={() => {
                        if (pendingToggle) {
                          setValues({
                            ...values,
                            isDisplayNutritionalInfo: true
                          });
                        }
                        setShowConfirmModal(false);
                        setPendingToggle(false);
                      }}
                    >
                      Agree
                    </Button>
                  </Modal.Footer>
                </Modal>
              </>
            );
          }}
        </Formik>
      </Col>

      <ExportFoodDataModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        userRole={currentUser?.role || 'clinic'}
        userClinicalExportEnabled={
          currentUser?.clinical_export_enabled || false
        }
        userResearchExportEnabled={
          currentUser?.research_export_enabled || false
        }
        userClinicId={userClinicId}
        fixedPatientId={id ? parseInt(id as string) : undefined}
      />
    </Row>
  );
};

export default EditPatient;
